import { Button } from '@/components/ui/button';
import clsx from 'clsx';
import { useEffect, useMemo, useRef } from 'react';
import { Temporal } from 'temporal-polyfill';
import {
  TimeMeridiem,
  FormInputTimeColumnsProps,
  computeValidTimeColumns,
  from24HourFormat,
  to24HourFormat,
} from './form-input-time-utils';

interface FormInputTimeColumnProps {
  options: string[];
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
}

export const FormInputTimeColumn = ({ options, value, onValueChange, className }: FormInputTimeColumnProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const heightClass = 'h-8'; //32 px, height of each button
  const itemHeightPx = parseInt(heightClass.replace('h-', '')) * 4;

  useEffect(() => {
    //Scroll to the selected option when the value changes or comes by default
    if (scrollRef.current && value) {
      const selectedIndex = options.indexOf(value);

      if (selectedIndex !== -1) {
        const scrollTop = itemHeightPx * selectedIndex;
        scrollRef.current.scrollTop = Math.max(0, scrollTop);
      }
    }
  }, [options, value, itemHeightPx]);

  return (
    <div className="flex flex-col w-16">
      <div
        ref={scrollRef}
        className="max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
      >
        {options.map((option) => (
          <Button
            key={option}
            variant="ghost"
            className={clsx(
              'w-full font-normal',
              heightClass,
              value === option && 'bg-blue-500 text-white hover:bg-blue-700 hover:text-white',
              className,
            )}
            onClick={() => onValueChange(option)}
          >
            {option}
          </Button>
        ))}
      </div>
    </div>
  );
};

export const FormInputTimeColumns = ({ value, onChange, step, min, max, className }: FormInputTimeColumnsProps) => {
  const { hour12, meridiem: meridiemSelected } = value
    ? from24HourFormat(value.hour)
    : { hour12: 12, meridiem: TimeMeridiem.AM };
  const hourSelected = hour12.toString().padStart(2, '0');
  const minuteSelected = value ? value.minute.toString().padStart(2, '0') : '00';

  const { validHours, validMinutes, validMeridiems } = useMemo(
    () => computeValidTimeColumns(step, min, max),
    [step, min, max],
  );

  const handleChange = (hour: string, minute: string, meridiem: TimeMeridiem) => {
    const hour24 = to24HourFormat(parseInt(hour, 10), meridiem);
    const plainTime = Temporal.PlainTime.from({ hour: hour24, minute: parseInt(minute, 10) });
    onChange(plainTime);
  };

  return (
    <div className="flex gap-1 items-start">
      <FormInputTimeColumn
        options={validHours}
        value={hourSelected}
        onValueChange={(hour) => handleChange(hour, minuteSelected, meridiemSelected)}
        className={className}
      />
      <FormInputTimeColumn
        options={validMinutes}
        value={minuteSelected}
        onValueChange={(minute) => handleChange(hourSelected, minute, meridiemSelected)}
        className={className}
      />
      <FormInputTimeColumn
        options={validMeridiems}
        value={meridiemSelected}
        onValueChange={(meridiem) => handleChange(hourSelected, minuteSelected, meridiem as TimeMeridiem)}
        className={className}
      />
    </div>
  );
};
